import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { MapPin, DollarSign, ArrowRight, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const featuredPrograms = [
  {
    name: "Master's in Business Administration - Health Care Services",
    location: 'Orlando, Florida',
    tuition: '$9,622/year',
    type: "Master's",
  },
  {
    name: 'Master of Arts in Clinical Counseling',
    location: 'Orlando, Florida',
    tuition: '$8,099/year',
    type: "Master's",
  },
  {
    name: 'Associate of Science in Paralegal Studies',
    location: 'Orlando, Miami, Tampa',
    tuition: '$9,480/year',
    type: "Associate's",
  },
  {
    name: 'Bachelor of Business Administration - AI Concentration',
    location: 'San Antonio, Texas',
    tuition: '$6,000/year',
    type: "Bachelor's",
  },
  {
    name: 'MS Engineering Management - Self-Design Program',
    location: 'Phoenix, Detroit, Reston',
    tuition: '$6,900/year',
    type: "Master's",
  },
  {
    name: 'MS Nursing - Nurse Leadership',
    location: 'Phoenix, Kansas, Wisconsin',
    tuition: '$16,110/year',
    type: "Master's",
  },
];

const FeaturedProgramsSection = () => {
  const { t } = useTranslation();

  return (
    <section className="py-24 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t('featuredPrograms.title')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('featuredPrograms.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {featuredPrograms.map((program, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="bg-card rounded-2xl p-6 h-full shadow-card border border-border/50 hover:border-primary/30 hover:shadow-lg transition-all duration-300 flex flex-col">
                <div className="flex items-start justify-between mb-4">
                  <div className="bg-primary/10 p-3 rounded-lg">
                    <GraduationCap className="h-6 w-6 text-primary" />
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {program.type}
                  </Badge>
                </div>

                <h3 className="text-lg font-semibold text-foreground mb-4 flex-1">
                  {program.name}
                </h3>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4 text-primary" />
                    {program.location}
                  </div>
                  <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <DollarSign className="h-4 w-4 text-primary" />
                    {program.tuition}
                  </div>
                </div>

                <Button asChild variant="outline" size="sm" className="w-full mt-auto">
                  <Link to="/programs">
                    {t('featuredPrograms.viewDetails')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center"
        >
          <Button asChild size="lg">
            <Link to="/programs">
              {t('featuredPrograms.viewAll')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturedProgramsSection;
